import { CalendarDays, LayoutList } from "lucide-react";
import { useTranslation } from "react-i18next";

function formatUsd(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(amount));
}

function formatDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function DealsPreview({ deals, market }) {
  const { t } = useTranslation();
  const items = Array.isArray(deals) ? deals : [];

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
            <CalendarDays className="h-3.5 w-3.5" />
            {t("app:AiFunding.dealsBadge")}
          </div>
          <h2 className="mt-3 text-xl font-semibold tracking-tight text-stone-950">
            {t("app:AiFunding.latestDeals")}
          </h2>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            {t("app:AiFunding.dealsDescription")}
          </p>
        </div>
        <div className="inline-flex h-8 shrink-0 items-center justify-center gap-2 rounded-full border border-stone-200 bg-white px-3 text-xs font-medium text-stone-700 shadow-sm">
          <LayoutList className="h-3.5 w-3.5" />
          {t("app:AiFunding.dealsCount", { count: market?.totalDeals ?? items.length })}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-5 rounded-lg border border-dashed border-stone-300 bg-stone-50/70 px-4 py-3 text-sm leading-6 text-stone-600">
          {t("app:AiFunding.noDeals")}
        </div>
      ) : (
        <ul className="mt-5 space-y-3">
          {items.slice(0, 8).map((deal) => (
            <li key={deal._id} className="rounded-lg border border-stone-200 bg-stone-50/70 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold leading-5 text-stone-950">
                    {deal.title || "—"}
                  </h3>
                  <div className="mt-2 text-xs text-stone-500">{formatDate(deal.date)}</div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                  {deal.amount ? (
                    <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      {formatUsd(deal.amount)}
                    </span>
                  ) : null}
                  {deal.round ? (
                    <span className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-700 ring-1 ring-stone-200">
                      {deal.round}
                    </span>
                  ) : null}
                </div>
              </div>

              {Array.isArray(deal.industries) && deal.industries.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {deal.industries.slice(0, 4).map((industry) => (
                    <span
                      key={`${deal._id}-${industry}`}
                      className="inline-flex max-w-full rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-600 ring-1 ring-stone-200"
                    >
                      <span className="truncate">{industry}</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
